// Marketplace Phase 1 — DemandTable (Feb 2026).
//
// Generic table for Top Brands / Top Product Categories / Top Sponsorship
// Categories. Each row: slug (with a friendly label resolved from
// catalog seed if available), count, source breakdown badge, and a
// week-over-week trend arrow (▲▼) when the previous snapshot is passed.
//
// No user IDs. No PII. Pure presentation.

import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

const Delta = ({ current, prev, t }) => {
  if (prev === undefined || prev === null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-400" title={t?.("marketplace.demand.noPrior") || ""}>
        <Minus className="h-3 w-3" /> —
      </span>
    );
  }
  const diff = current - prev;
  if (diff === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-slate-500">
        <Minus className="h-3 w-3" /> 0
      </span>
    );
  }
  if (diff > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700 font-medium">
        <TrendingUp className="h-3 w-3" /> +{diff}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-rose-700 font-medium">
      <TrendingDown className="h-3 w-3" /> {diff}
    </span>
  );
};

export const DemandTable = ({ title, items, prevItems, resolveLabel, t, testIdSuffix }) => {
  const prevMap = React.useMemo(() => {
    const m = {};
    (prevItems || []).forEach((it) => { m[it.slug] = it.count; });
    return m;
  }, [prevItems]);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white overflow-hidden"
      data-testid={`demand-table-${testIdSuffix}`}
    >
      <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
        <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
      </div>
      {(!items || items.length === 0) ? (
        <div className="p-6 text-center text-sm text-slate-400" data-testid={`demand-table-${testIdSuffix}-empty`}>
          {t("marketplace.demand.tableEmpty")}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500">
            <tr>
              <th className="text-start px-4 py-2">{t("marketplace.demand.colLabel")}</th>
              <th className="text-end px-4 py-2">{t("marketplace.demand.colCount")}</th>
              <th className="text-end px-4 py-2">Δ</th>
              <th className="text-end px-4 py-2 hidden sm:table-cell">{t("marketplace.demand.colSource")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => {
              const breakdown = row.source_breakdown || {};
              const cat = breakdown.catalog || 0;
              const fuz = breakdown.fuzzy_rescue || 0;
              const pro = breakdown.custom_other_promotion || 0;
              return (
                <tr
                  key={row.slug}
                  className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/30"}
                  data-testid={`demand-row-${testIdSuffix}-${row.slug}`}
                >
                  <td className="px-4 py-2">
                    <div className="text-slate-900">{resolveLabel(row.slug)}</div>
                    <div className="text-[10px] font-mono text-slate-400">{row.slug}</div>
                  </td>
                  <td className="px-4 py-2 text-end font-mono font-semibold text-slate-900">
                    {row.count.toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-end">
                    <Delta current={row.count} prev={prevMap[row.slug]} t={t} />
                  </td>
                  <td className="px-4 py-2 text-end text-[10px] font-mono text-slate-500 hidden sm:table-cell">
                    {cat > 0 && <span className="inline-block px-1.5 py-0.5 me-1 rounded bg-emerald-50 text-emerald-700">cat:{cat}</span>}
                    {fuz > 0 && <span className="inline-block px-1.5 py-0.5 me-1 rounded bg-amber-50 text-amber-700">fuz:{fuz}</span>}
                    {pro > 0 && <span className="inline-block px-1.5 py-0.5 me-1 rounded bg-sky-50 text-sky-700">pro:{pro}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default DemandTable;
